import { showError } from "app/slice";
import type { appStore } from "app/store";

type Store = typeof appStore;

/**
 * Heuristic: was this error caused by the browser failing to reach the
 * server? These come through as TypeError with messages like
 * "Failed to fetch", "NetworkError when attempting to fetch resource",
 * or "Load failed". We intentionally do not block on these in the UI
 * because the user has a clear, actionable recovery (check connection,
 * retry / reload).
 */
const isNetworkError = (reason: unknown): boolean => {
  if (reason instanceof TypeError) {
    return /failed to fetch|networkerror|load failed|network request failed/i.test(reason.message);
  }
  return false;
};

/**
 * Noisy errors we want to silently swallow rather than show in a modal.
 * ResizeObserver loop warnings are benign and surface in many React apps.
 */
const isIgnorable = (message: string): boolean => {
  return /resizeobserver loop|script error\.?$/i.test(message);
};

const dispatchFriendlyError = (store: Store, network: boolean) => {
  store.dispatch(
    showError({
      title: network ? "Connection Problem" : "Unexpected Error",
      message: network
        ? "We couldn't reach the server. Please check your connection and try again. " +
          "If the problem persists, the service may be temporarily unavailable."
        : "Something unexpected happened. Reloading the page usually fixes it. " +
          "If it keeps happening, please contact support.",
      critical: false,
    }),
  );
};

/**
 * Installs window-level handlers that route uncaught errors and rejected
 * promises into the same friendly ErrorModal users already see for known
 * failures — instead of leaving them with the CRA dev overlay (in dev) or
 * a silent failure (in prod).
 *
 * Registered on the capture phase so we can preventDefault + stop
 * propagation before react-error-overlay sees the event.
 */
export const installGlobalErrorHandlers = (store: Store): void => {
  if (typeof window === "undefined") return;

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const network = isNetworkError(reason);
      const message =
        reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";

      if (isIgnorable(message)) {
        event.preventDefault();
        return;
      }

      console.error("Unhandled promise rejection:", reason);
      dispatchFriendlyError(store, network);
      event.preventDefault();
    },
    true,
  );

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      const message = event.error?.message || event.message || "";
      if (isIgnorable(message)) {
        event.preventDefault();
        return;
      }

      // Resource load errors (img/script failed to load) come through here
      // with no event.error — usually safe to ignore for our UX.
      if (!event.error) return;

      const network = isNetworkError(event.error);
      console.error("Uncaught error:", event.error);
      dispatchFriendlyError(store, network);
      event.preventDefault();
    },
    true,
  );
};
